import { Fragment, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Separator } from '@/components/ui/separator';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { APP_NAME } from '@/lib/brand';
import { AppSidebar } from './app-sidebar';
import { APP_NAV } from './nav';
import { ThemeToggle } from './theme-toggle';
import { UserMenu } from './user-menu';
import { useCurrentApp } from './use-current-app';

type Crumb = { label: string; to?: string };

// The sidebar writes its expanded/collapsed state to this cookie; read it back so a reload keeps it.
const sidebarStartsOpen = () => !document.cookie.split('; ').includes('sidebar_state=false');

function useCrumbs(): Crumb[] {
  const { pathname } = useLocation();
  const { route, env, app } = useCurrentApp();
  if (pathname === '/ops') return [{ label: 'Queues & DLQ' }];
  if (pathname === '/resources') return [{ label: 'Resources' }];
  if (pathname === '/apps/new') return [{ label: 'Applications', to: '/' }, { label: 'New application' }];
  if (route) {
    const section = APP_NAV.find((n) => n.to === route.section)?.label ?? 'Overview';
    return [{ label: 'Applications', to: '/' }, { label: app?.name ?? '…', to: `/apps/${route.appId}?env=${env}` }, { label: section }];
  }
  return [{ label: 'Applications' }];
}

export function AppShell() {
  const crumbs = useCrumbs();
  const { pathname } = useLocation();
  // A new page starts at the top; staying at the old offset made short pages appear to jump.
  useEffect(() => { window.scrollTo({ top: 0 }); }, [pathname]);
  // Tab title from the breadcrumbs, most specific first ("Credentials · My App · Rayo …"), so several
  // open tabs can be told apart. The leading "Applications" crumb is dropped once there's more detail.
  const labels = (crumbs.length > 1 ? crumbs.slice(1) : crumbs).map((c) => c.label).filter((l) => l !== '…');
  const titleText = [...labels.reverse(), APP_NAME].join(' · ');
  useEffect(() => { document.title = titleText; }, [titleText]);
  return (
    <SidebarProvider defaultOpen={sidebarStartsOpen()}>
      <AppSidebar />
      {/* Full-bleed: the content fills the window beside the sidebar (no inset card, no max width).
          The window scrolls and the header sticks to its top. */}
      <SidebarInset className="min-w-0">
        <header className="sticky top-0 z-10 flex h-14 shrink-0 items-center gap-2 border-b bg-background px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-1 data-[orientation=vertical]:h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              {crumbs.map((c, i) => (
                // Phones show only the current page; the sidebar covers the rest.
                <Fragment key={i}>
                  {i > 0 && <BreadcrumbSeparator className="hidden md:block" />}
                  <BreadcrumbItem className={i < crumbs.length - 1 ? 'hidden md:inline-flex' : undefined}>
                    {c.to ? <BreadcrumbLink asChild><Link to={c.to}>{c.label}</Link></BreadcrumbLink> : <BreadcrumbPage>{c.label}</BreadcrumbPage>}
                  </BreadcrumbItem>
                </Fragment>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
          <div className="ml-auto flex items-center gap-1"><ThemeToggle /><UserMenu /></div>
        </header>
        <main className="w-full min-w-0 flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
